import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { useAuth } from "@/context/AuthContext";
import PackagingLabellingInitial from "@/static/initial/OtherLabeling";
import { IPackagingData } from "@/components/ActiveProjects/OtherLabeling/types/OtherLabeling.types";
import PackagingService from "@/services/packagingService";
import PackagingOnloadService from "@/services/packagingOnloadService";
import StorageService from "@/services/storageService";
import api from "@/services/api";
import { mapOnloadApiToPackagingData } from "@/components/ActiveProjects/OtherLabeling/hooks/usePackagingOnload";
import mapPackagingFormToApi from "@/components/ActiveProjects/OtherLabeling/utils/mapPackagingFormToApi";
import type {
  DiscontinueDataPayload,
  NotProgressedDataPayload,
} from "@/components/ActiveProjects/shared/utils/mapDiscontinuationFormToDiscontinueData";
import { getWorkflowTransitionIdByLabel } from "@/utils/helper";
import transitions from "@/constants/transitions";
import { ActionType } from "@/types/Common.types";
import WorkflowTransitionService from "@/services/workflowTransitionService";
import { WorkflowTransitionPayload } from "@/types/WorkflowTransitionService.types";
import { shouldShowVariantTabs } from "@/constants/tabStructure";
import { normalizeSectionNameOrDefault } from "@/utils/sectionNaming";
import { mapDiscontinuationReasonOnloadOptions } from "@/components/ActiveProjects/shared/utils/mapDiscontinuationFormToDiscontinueData";
import useActiveProjectValidation from "@/components/ActiveProjects/validation/useActiveProjectValidation";
import { useUserType } from "@/hooks/useUserType";

const mapSimple = (arr?: Array<{ id: any; name: any }>) =>
  Array.isArray(arr) ? arr.map(i => ({ label: String(i?.name ?? i?.id ?? ''), value: String(i?.id ?? i?.name ?? '') })) : [];

const mapSimpleName = (arr?: Array<{ id: any; name: any }>) =>
  Array.isArray(arr) ? arr.map(i => ({ label: String(i?.name ?? i?.id ?? ''), value: String(i?.name ?? i?.id ?? '') })) : [];

const mapCodingStatementOptions = (arr?: any[]) => {
  if (!Array.isArray(arr)) return [];
  return arr.map((item) => ({
    id: item?.id ?? item?.value ?? item?.name ?? "",
    label: String(item?.label ?? item?.name ?? item?.id ?? "").trim(),
    value: String(item?.value ?? item?.name ?? item?.id ?? "").trim(),
  }));
};

const usePackaging = (sectionId?: string | number, vendorAssessmentId?: string | number, skipAutoLoad?: boolean) => {
  
  const { activeProjectsProductInfo, selectedActiveProject, selectedActiveProjectSelectedVendor, startLoading, stopLoading } = useGlobalStateContext();
  const { userDetails } = useAuth();
  const [data, setData] = useState<IPackagingData>(PackagingLabellingInitial({}));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dropdowns, setDropdowns] = useState<Record<string, Array<{ label: string; value: string }>>>({});
  const [requestForRevisionNotes, setRequestForRevisionNotes] = useState<{ notes: string; id?: string | number }>({ notes: '', id: undefined });
  const isLoadingRef = useRef(false); // Track if loadPackaging is currently being called
  const lastLoadedSectionIdRef = useRef<string | number | undefined>(undefined); // Track last loaded sectionId to prevent duplicate loads
  const active_projects_transitions = transitions.active_projects;
  const { updateSectionValidationFlag } = useActiveProjectValidation();
  const userType = useUserType();
  const isTJ = userType === "TJ";
  const isVendor = userType === "VENDOR";
  const specType = useMemo((): string | undefined => {
    const t = selectedActiveProject?.productSpecificationType || selectedActiveProject?.category || '';
    const normalized = String(t || '').replace(/&amp;/g, "&").trim();
    if (normalized === "HABA (Health and Beauty Aids)" || normalized === "HABA" || normalized.toLowerCase().includes('haba')) {
      return '?type=HABA';
    } else if (normalized === "Pet Foods") {
      return '?type=Pet Foods';
    }
    return undefined;
  }, [selectedActiveProject?.productSpecificationType, selectedActiveProject?.category]);

  const resolveProductSpecificationId = useCallback((sectionId?: string | number): string | number | null => {
    try {
      const info = activeProjectsProductInfo;
      if (!info || !Array.isArray(info?.vendorDetails) || info.vendorDetails.length === 0) return null;

      const selectedVendorId = String(selectedActiveProjectSelectedVendor ?? "");
      const vendor = info.vendorDetails.find((v: any) => String(v?.vendorId) === selectedVendorId) || info.vendorDetails[0];
      const specs: any[] = vendor?.vendorIngredientDetails?.productSpecification || [];
      if (!Array.isArray(specs) || specs.length === 0) return null;

      const variants: any[] = Array.isArray((selectedActiveProject as any)?.variants) ? (selectedActiveProject as any).variants : [];
      let index = 0;
      if (variants.length > 0 && (sectionId ?? null) !== null && (sectionId ?? undefined) !== undefined) {
        const idx = variants.findIndex((v: any) => String(v?.id) === String(sectionId));
        index = idx >= 0 ? idx : 0;
      }
      else if((sectionId ?? null) !== null) {
        const specIdx = specs.findIndex((spec: any) => String(spec?.id) === String(sectionId));
        index = specIdx >= 0 ? specIdx : 0;
      }
      const spec = specs[index] || specs[0];
      return spec?.id ?? null;
    } catch {
      return null;
    }
  }, [activeProjectsProductInfo, selectedActiveProject, selectedActiveProjectSelectedVendor]);

  const loadPackaging = useCallback(async (section?: string | number, forceReload?: boolean) => {
    const sectionKey = section ?? sectionId;
    if(!sectionKey) return;
    // Prevent duplicate calls - check if already loading or if this section was just loaded
    if (isLoadingRef.current) {
      return;
    }
    
    // Prevent loading the same sectionId if it was just loaded (unless forceReload is true)
    if (!forceReload && lastLoadedSectionIdRef.current === sectionKey && !isLoadingRef.current) {
      return;
    }
    
    isLoadingRef.current = true;
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
    setLoading(true);
    startLoading();
    setError(null);
    try {
      const parsedSectionId = typeof section === 'string' ? parseInt(section, 10) : (section ?? 0);
      // const productSpecId = resolveProductSpecificationId(parsedSectionId);
      if (parsedSectionId === null || parsedSectionId === undefined) throw new Error('Missing product specification id');

      // GET packaging details via API (supports vendorAssessmentId)
      const { productServiceAPI } = api as any;
      const url = `/product/other-labeling-tab/${parsedSectionId}`;
      const respRaw = await productServiceAPI.get(url);
      const resp = respRaw?.data ?? {};

     

      // Normalize response to IPackagingData using mapper then initializer
      const normalized = resp || {};
      const mapped = mapOnloadApiToPackagingData(normalized);
      const hydrated = PackagingLabellingInitial(mapped) as any;
      setDropdowns((prev) => ({...prev, vendorManufacturingFacilities: mapSimple(resp?.vendorManufacturingFacilities || [])}));
      // Preserve sectionName from API response if present
      if (normalized?.sectionName !== undefined) {
        hydrated.sectionName = normalized.sectionName;
      }
      hydrated.otherInfo = { hasCopyEPRData: normalized?.hasCopyEPRData || false }
      const getIds = (arr: any): Array<string | number> => {
        if (!Array.isArray(arr)) return [];
        return arr
          .map((x: any) => (x && typeof x === 'object' ? x.id : x))
          .filter((id: any) => id !== undefined && id !== null);
      };
      hydrated.__original = {
        recyclingIcons: getIds(resp?.recyclingIcons),
        productSpecificIconsStamps: getIds(resp?.productSpecificIconsStamps || resp?.productSpecificIconsLegends),
        kosherSymbol: getIds(resp?.kosherSymbol),
        kosherDesignation: getIds(resp?.kosherDesignation),
        claimsDisclaimerStatements: getIds(resp?.claimsDisclaimerStatements),
        safetyProcessingEnvStatements: getIds(resp?.safetyProcessingEnvStatements),
        storageHandlingDistributionStatements: getIds(resp?.storageHandlingDistributionStatements),
        codingStatements: getIds(resp?.codingStatement),
        countryOfOriginStatements: getIds(resp?.countryOfOriginStatements),
        printerDetails: getIds(resp?.printerDetails || resp?.printerDielineDetails),
        organicClaim: getIds(resp?.organicClaim),
        organicCertifier: getIds(resp?.organicCertifier),
        bottleRedemptionInformation: getIds(resp?.bottleRedemptionInformation).map(String),
        productLabelingDetailsIds: getIds(resp?.productLabelingDetails),
        iconsAndClaims: getIds(resp?.iconsAndClaims || resp?.iconclaims),
        onPackIcons: getIds(resp?.onPackIcons),
        // availableTransitions: getIds(resp?.availableTransitions?.data || [])
      };
      
      // Load revision notes from API response (array format, take last item)
      if (resp?.revisionNotes) {
        if (Array.isArray(resp.revisionNotes) && resp.revisionNotes.length > 0) {
          const lastRevisionNote = resp.revisionNotes[resp.revisionNotes.length - 1];
          setRequestForRevisionNotes({ notes: lastRevisionNote.notes || '', id: lastRevisionNote.id });
        } else {
          setRequestForRevisionNotes({ notes: '', id: undefined });
        }
      } else {
        setRequestForRevisionNotes({ notes: '', id: undefined });
      }
      
     
      
      setData(hydrated);
      return hydrated;
    } catch (e: any) {
      setError(e?.message || 'Failed to load packaging');
      const empty = PackagingLabellingInitial({});
      setData(empty);
      return empty;
    } finally {
      setLoading(false);
      stopLoading();
      isLoadingRef.current = false;
      // Update last loaded sectionId after successful load
      lastLoadedSectionIdRef.current = sectionKey;
    }
  }, [resolveProductSpecificationId, vendorAssessmentId, selectedActiveProject, sectionId]);

   // Load onload dropdowns
  useEffect(() => {
    const loadOnload = async () => {
      const [onload, storageOnload] = await Promise.all([
        PackagingOnloadService.getOnload(specType),
        StorageService.getStorageOnload().catch(() => null),
      ]);
    

    const mapWithChildren = (arr?: Array<{ id: any; name: any; children: any[] }>) =>
        Array.isArray(arr) ? arr.map(i => ({ label: String(i?.name ?? i?.id ?? ''), value: String(i?.name ?? i?.id ?? ''), children: mapSimpleName(i?.children || []) })) : [];
      
      // Special mapping for OnPackIcons: use id as value (for payload) and name as label (for display)
      const mapOnPackIcons = (arr?: Array<{ id: any; name: any }>) =>
        Array.isArray(arr) ? arr.map(i => ({ 
          label: String(i?.name ?? i?.id ?? ''), 
          value: String(i?.id ?? '') 
        })) : [];
      
      setDropdowns((prev) => ({...prev,
        CandleWarningPhrase: mapSimple(onload?.CandleWarningPhrase),
        RecyclingIconsText: mapSimple(onload?.RecyclingIconsText),
        CosmeticWarningPhrase: mapSimple(onload?.CosmeticWarningPhrase),
        UseOnPack: mapSimple(onload?.UseOnPack),
        OnPackIcons: mapOnPackIcons(onload?.onPackIcons),
        SafetyWarningPhrase: Array.isArray(onload?.SafetyWarningPhrase)
          ? onload.SafetyWarningPhrase.map((i: any) => ({
              id: String(i?.name ?? i?.id ?? ''),
              label: String(i?.name ?? i?.id ?? '').replace(/\{l\}/g, '{}'),
              value: String(i?.id ?? i?.name ?? '')
            }))
          : [],
        NutritionDietaryIcons: mapSimple(onload?.NutritionDietaryIcons),
        ClaimOrStatement: Array.isArray(onload?.ClaimOrStatement)
          ? onload.ClaimOrStatement.map((i: any) => ({
              id: String(i?.name ?? i?.id ?? ''),
              label: String(i?.name ?? i?.id ?? '').replace(/\{l\}/g, '{}'),
              value: String(i?.name ?? i?.id ?? ''),
            }))
          : [],
        DesignTeam: mapSimple(onload?.DesignTeam),
        countryList: mapSimple(onload?.countryList),
        BottleRedemptionInformation:  Array.isArray(onload?.BottleRedemptionInformation)
        ? onload.BottleRedemptionInformation.map((i: any) => ({
            id: String(i?.name ?? i?.id ?? ''),
            label: String(i?.name ?? i?.id ?? ''),
            value: String(i?.name ?? i?.id ?? '')
          }))
        : [],
        PackagingMaterial: mapWithChildren(onload?.PackagingMaterial),
        MaterialSubclass: [],
        CountryOriginStatement: Array.isArray(onload?.CountryOriginStatement)
          ? onload.CountryOriginStatement.map((i: any) => ({
              label: String((i?.name ?? '').replace(/\(1\)/g, '{}') || i?.id || ''),
              value: String(i?.id ?? i?.name ?? '')
            }))
          : [],
        PackagingLevels: mapSimpleName(onload?.PackagingLevels),
        PackagingComponent: mapSimpleName(onload?.PackagingComponent),
        ProductSpecificIcons: mapSimple(onload?.ProductSpecificIcons),
        RegulatoryGovernance: mapSimple(onload?.RegulatoryGovernance),
        StorageStatementText: Array.isArray(onload?.StorageStatementText)
          ? onload.StorageStatementText.map((i: any) => ({
              id: String(i?.name ?? i?.id ?? ''),
              label: String(i?.name ?? i?.id ?? '').replace(/\{s\}/g, '{}').replace(/\{m\}/g, '{}').replace(/\{l\}/g, '{}'),
              value: String(i?.id ?? i?.name ?? '')
            }))
          : [],
        OrganicProductsIcons: mapSimpleName(onload?.OrganicClaim),
        OrganicCertifierIcons: mapSimpleName(onload?.OrganicCertifier),
        ReasonForCountryOfOrigin: mapSimple(onload?.ReasonForCountryOfOrigin),
        PetFoodClaimsStatements: Array.isArray(onload?.PetFoodClaimsStatements)
          ? onload.PetFoodClaimsStatements.map((i: any) => ({ 
              id: String(i?.name ?? i?.id ?? ''),
              label: String(i?.label ?? i?.name ?? i?.id ?? '').replace(/\{m\}/g, '{}') 
            }))
          : [],

      CompostableStandards: mapSimple(onload?.CompostableStandards),
      KosherDesignation: mapSimpleName(onload?.KosherDesignation || []),
      KosherSymbol: mapSimpleName(onload?.KosherSymbol || []),
      ReasonForDiscontinuation: mapDiscontinuationReasonOnloadOptions(
        onload?.discontinuationReason
      ),
      CodingStatement: mapCodingStatementOptions(
        storageOnload?.prePrintedShelfLifeCodingStatement
      ),
     
      }));
    };
    loadOnload();
  }, [specType]);


  // Helpers to resolve transitionIds from available transitions (prefer TM then NT where applicable)
  const normalizeAvailableTransitions = useCallback((avail: any) => {
    // Supported shapes across the app:
    // - Array<{label, transitionId}>
    // - { data: Array<{label, transitionId}> }
    // - { transitions: { data: Array<{label, transitionId}> } }
    // - { availableTransitions: { data: Array<{label, transitionId}> } }  (some endpoints wrap like this)
    if (Array.isArray(avail)) return { data: avail } as any;
    if (Array.isArray(avail?.data)) return avail as any;
    if (Array.isArray(avail?.transitions?.data)) return { transitions: { data: avail.transitions.data } } as any;
    if (Array.isArray(avail?.availableTransitions?.data)) return { data: avail.availableTransitions.data } as any;
    return avail as any;
  }, []);

  const getShareForReviewTransitionId = useCallback((avail: any): string | undefined => {
    // Packaging workflow is now centralized: Share-for-review should come from the Packaging listing transitions.
    // Backend may send any of these labels; we use the first available (in this order).
    const shareForReviewTransitions = [
      active_projects_transitions.share_for_review,
      active_projects_transitions.vo_ps_data_review,
      active_projects_transitions.share_for_review_data,
      active_projects_transitions.nt_share_for_review,
      active_projects_transitions.share_for_review_food_safety_qa,
      active_projects_transitions.tm_share_for_review,
      active_projects_transitions.version_share_for_review,
    ];
    
    const transitionsObj = normalizeAvailableTransitions(avail);
    
    for (const transition of shareForReviewTransitions) {
      const transitionId = getWorkflowTransitionIdByLabel(transitionsObj, transition);
      if (transitionId) {
        return transitionId;
      }
    }
    
    return undefined;
  }, [
    normalizeAvailableTransitions,
    active_projects_transitions.share_for_review,
    active_projects_transitions.vo_ps_data_review,
    active_projects_transitions.share_for_review_data,
    active_projects_transitions.nt_share_for_review,
    active_projects_transitions.share_for_review_food_safety_qa,
    active_projects_transitions.tm_share_for_review,
    active_projects_transitions.version_share_for_review,
  ]);

  // Returns both NT and TM transition IDs if available, or a single ID if only one is present
  const getPackagingReviewCompleteTransitionIds = useCallback((avail: any): { ntId?: string; tmId?: string, qaId?: string } => {
    const transitionsObj = normalizeAvailableTransitions(avail);
    // New unified Packaging GET labels
    const tmId = getWorkflowTransitionIdByLabel(transitionsObj, active_projects_transitions.tm_review_complete);
    const ntId = getWorkflowTransitionIdByLabel(transitionsObj, active_projects_transitions.nt_review_complete);
    // New unified "data" review-complete label
    const dataId = getWorkflowTransitionIdByLabel(transitionsObj, active_projects_transitions.review_complete_data);
    // Packaging can be reviewed by Food Safety QA in some flows
    const qaId = getWorkflowTransitionIdByLabel(
      transitionsObj,
      active_projects_transitions.review_complete_food_safety_qa
    );
    const glcId = getWorkflowTransitionIdByLabel(transitionsObj, active_projects_transitions.approve);
    const vendorAckId = getWorkflowTransitionIdByLabel(transitionsObj, active_projects_transitions.acknowledge);
    const baseNt = ntId ?? dataId ?? qaId ?? glcId;
    // Vendor "acknowledge" when no TJ review-complete slot is present; do not pair vendorAck as NT with TM dual-step.
    const mergedNt = baseNt ?? (!tmId ? vendorAckId : undefined);
    return { ntId: mergedNt, tmId };
  }, [
    normalizeAvailableTransitions,
    active_projects_transitions.tm_review_complete,
    active_projects_transitions.nt_review_complete,
    active_projects_transitions.review_complete_data,
    active_projects_transitions.review_complete_food_safety_qa,
    active_projects_transitions.approve,
    active_projects_transitions.acknowledge,
  ]);

  // Returns both NT and TM transition IDs for "Review again" if available
  const getPackagingReviewAgainTransitionIds = useCallback((avail: any): { ntId?: string; tmId?: string, qaId?: string } => {
    const transitionsObj = normalizeAvailableTransitions(avail);
    const tmId = getWorkflowTransitionIdByLabel(transitionsObj, active_projects_transitions.tm_review_again);
    const ntId = getWorkflowTransitionIdByLabel(transitionsObj, active_projects_transitions.nt_review_again);
    const qaId = getWorkflowTransitionIdByLabel(transitionsObj, active_projects_transitions.qa_review_again);
    return { ntId, tmId, qaId };
  }, [normalizeAvailableTransitions, active_projects_transitions.tm_review_again, active_projects_transitions.nt_review_again, active_projects_transitions.qa_review_again]);

  const getPackagingRequestRevisionTransitionId = useCallback((avail: any): string | undefined => {
    const transitionsObj = normalizeAvailableTransitions(avail);
    const labels = [
      active_projects_transitions.request_revisions,
      active_projects_transitions.request_for_revision_data,
      active_projects_transitions.request_for_revision_food_safety_qa,
      active_projects_transitions.nt_request_for_revision,
      active_projects_transitions.tm_request_for_revision,
    ].filter(Boolean);
    for (const label of labels) {
      const id = getWorkflowTransitionIdByLabel(transitionsObj, label);
      if (id) return id;
    }
    return undefined;
  }, [normalizeAvailableTransitions, active_projects_transitions]);

  // Auto-load whenever hook is used or section changes (unless skipAutoLoad is true)
  useEffect(() => {
    if (skipAutoLoad) {
      return; // Skip auto-load when flag is set
    }
    // Prevent duplicate calls if already loading
    if (isLoadingRef.current) {
      return;
    }
    // Prevent loading if this sectionId was just loaded
    if (lastLoadedSectionIdRef.current === sectionId) {
      return;
    }
    loadPackaging(sectionId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, resolveProductSpecificationId, skipAutoLoad]);

  const savePackaging = useCallback(async (
    payload: any,
    section?: string | number,
    availableTransitions?: any,
    actionType?: ActionType,
    sectionIndex: number = 0,
    transitionOptions?: {
      revisionNoteIdOverride?: string | number;
      reasonForVersion?: string;
      reasonForRejectVersion?: string;
      /** When listing returns no transition rows (e.g. version_cloning), use id from fresh snapshot or this override */
      requestNewVersionTransitionIdOverride?: string;
      /** GLC Request Updates — revision note text on packaging save payload */
      glcRevisionNote?: string;
      /** Prefer transition id from page (e.g. Packaging memo) when GET transitions omit rows */
      requestUpdatesTransitionIdOverride?: string;
      /** Launched Products — discontinue product payload */
      discontinueData?: DiscontinueDataPayload;
      requestToDiscontinuedTransitionIdOverride?: string;
      cancelToDiscontinuedProcessTransitionIdOverride?: string;
      proceedToDiscontinuedTransitionIdOverride?: string;
      /** Active Projects — cancel project (not progressed) payload */
      notProgressedData?: NotProgressedDataPayload;
    }
  ) => {
    setLoading(true);
    startLoading();
    setError(null);
    try {
      const productSpecId = typeof section === 'string' ? parseInt(section, 10) : (section ?? sectionId ?? 0);
      if (productSpecId === null || productSpecId === undefined) throw new Error('Missing product specification id');

      const productSpecType = selectedActiveProject?.productSpecificationType || selectedActiveProject?.category || '';
      // Determine if this is a multipack product to conditionally include sectionName
      const shouldShowSectionTabs = shouldShowVariantTabs(selectedActiveProject);
      const options = { levels: dropdowns?.PackagingLevels || [], isVendor };
      const mappedPayload = mapPackagingFormToApi(payload, productSpecType, options);

      // Handle review complete / review again with special logic for both transitions
      if (availableTransitions && (actionType === "reviewComplete" || actionType === "reviewAgain")) {
        const { ntId, tmId, qaId } =
          actionType === "reviewAgain"
            ? getPackagingReviewAgainTransitionIds(availableTransitions)
            : getPackagingReviewCompleteTransitionIds(availableTransitions);

        // If both transitions are present, use NT transition for save, then call TM transition endpoint
        if (ntId || tmId || qaId) {
          const transitionId = tmId || ntId || qaId;
          if (transitionId) {
            mappedPayload.transitionRequest = { objectId: vendorAssessmentId, transitionId } as any;
          }
        }
      }
      // Attach transitionObject for other workflow actions
      else if (
        actionType &&
        actionType !== "save" &&
        actionType !== "next" &&
        (availableTransitions ||
          actionType === "requestNewVersion" ||
          actionType === "versionRequestApprove" ||
          actionType === "versionRequestReject" ||
          actionType === "requestUpdates" ||
          actionType === "requestToDiscontinued" ||
          actionType === "cancelDiscontinuationRequest" ||
          actionType === "completeDiscontinuation")
      ) {
        let transitionId: string | undefined;
        if (actionType === "shareForReview") {
          transitionId = getShareForReviewTransitionId(availableTransitions);
        } else if (actionType === "requestForRevision") {
          transitionId = getPackagingRequestRevisionTransitionId(availableTransitions);
        } else if (actionType === "requestNewVersion") {
          const transitionsObj = normalizeAvailableTransitions(availableTransitions);
          const override = transitionOptions?.requestNewVersionTransitionIdOverride;
          const trimmedOverride =
            typeof override === "string" && override.trim() !== "" ? override.trim() : undefined;
          transitionId =
            trimmedOverride ??
            getWorkflowTransitionIdByLabel(transitionsObj, active_projects_transitions.request_version);
          if (!transitionId && typeof active_projects_transitions.request_version === "string") {
            transitionId = active_projects_transitions.request_version;
          }
        } else if (actionType === "versionRequestApprove") {
          const arr = Array.isArray(availableTransitions) ? availableTransitions : [];
          const hit = arr.find(
            (t: { transitionId?: string; label?: string }) =>
              String(t?.transitionId ?? "") === "activity_version_request_approve" &&
              String(t?.label ?? "").toLowerCase() === "approve"
          );
          transitionId = hit?.transitionId;
        } else if (actionType === "versionRequestReject") {
          const arr = Array.isArray(availableTransitions) ? availableTransitions : [];
          const hit = arr.find(
            (t: { transitionId?: string; label?: string }) =>
              String(t?.transitionId ?? "") === "reject_version" &&
              String(t?.label ?? "").toLowerCase() === "reject_version"
          );
          transitionId = hit?.transitionId;
        } else if (actionType === "requestUpdates") {
          const transitionsObj = normalizeAvailableTransitions(availableTransitions);
          const override = transitionOptions?.requestUpdatesTransitionIdOverride;
          const trimmedOverride =
            typeof override === "string" && override.trim() !== "" ? override.trim() : undefined;
          transitionId =
            trimmedOverride ??
            getWorkflowTransitionIdByLabel(transitionsObj, active_projects_transitions.request_updates);
        } else if (actionType === "requestToDiscontinued") {
          const transitionsObj = normalizeAvailableTransitions(availableTransitions);
          const override = transitionOptions?.requestToDiscontinuedTransitionIdOverride;
          const trimmedOverride =
            typeof override === "string" && override.trim() !== "" ? override.trim() : undefined;
          transitionId =
            trimmedOverride ??
            getWorkflowTransitionIdByLabel(
              transitionsObj,
              active_projects_transitions.request_to_discontinued
            );
        } else if (actionType === "cancelDiscontinuationRequest") {
          const transitionsObj = normalizeAvailableTransitions(availableTransitions);
          const override = transitionOptions?.cancelToDiscontinuedProcessTransitionIdOverride;
          const trimmedOverride =
            typeof override === "string" && override.trim() !== "" ? override.trim() : undefined;
          transitionId =
            trimmedOverride ??
            getWorkflowTransitionIdByLabel(
              transitionsObj,
              active_projects_transitions.cancel_to_discontinued_process
            ) ??
            getWorkflowTransitionIdByLabel(
              transitionsObj,
              active_projects_transitions.return_to_discontinued
            );
        } else if (actionType === "completeDiscontinuation") {
          const transitionsObj = normalizeAvailableTransitions(availableTransitions);
          const override = transitionOptions?.proceedToDiscontinuedTransitionIdOverride;
          const trimmedOverride =
            typeof override === "string" && override.trim() !== "" ? override.trim() : undefined;
          transitionId =
            trimmedOverride ??
            getWorkflowTransitionIdByLabel(
              transitionsObj,
              active_projects_transitions.proceed_to_discontinued
            );
        }
        if (actionType === "requestNewVersion" && transitionOptions?.reasonForVersion != null) {
          const rv = String(transitionOptions.reasonForVersion).trim();
          if (rv) {
            (mappedPayload as any).reasonForVersion = rv;
          }
        }
        if (actionType === "requestUpdates" && transitionOptions?.glcRevisionNote != null) {
          const gn = String(transitionOptions.glcRevisionNote).trim();
          if (gn) {
            (mappedPayload as any).glcRevisionNote = gn;
          }
        }
        if (
          (actionType === "requestNewVersion" || actionType === "versionRequestReject") &&
          vendorAssessmentId != null &&
          String(vendorAssessmentId).trim() !== ""
        ) {
          (mappedPayload as any).vendorAssessmentId = String(vendorAssessmentId).trim();
        }
        if (
          actionType === "requestUpdates" &&
          vendorAssessmentId != null &&
          String(vendorAssessmentId).trim() !== ""
        ) {
          const existing = (mappedPayload as any).vendorAssessmentId;
          if (existing === undefined || existing === null || String(existing).trim() === "") {
            const n = Number(vendorAssessmentId);
            (mappedPayload as any).vendorAssessmentId = Number.isFinite(n) ? n : String(vendorAssessmentId).trim();
          }
        }
        if (actionType === "versionRequestReject" && transitionOptions?.reasonForRejectVersion != null) {
          const rj = String(transitionOptions.reasonForRejectVersion).trim();
          if (rj) {
            (mappedPayload as any).reasonForRejectVersion = rj;
          }
        }
        if (transitionId) {
          const tr: any = { objectId: vendorAssessmentId, transitionId };

          // RFR: attach revisionNoteId into transitionRequest (required by backend)
          const revisionNoteIdSource =
            actionType === "requestForRevision"
              ? (transitionOptions?.revisionNoteIdOverride ?? requestForRevisionNotes?.id)
              : actionType === "shareForReview"
                ? transitionOptions?.revisionNoteIdOverride
                : undefined;

          if (revisionNoteIdSource !== undefined && revisionNoteIdSource !== null) {
            const raw = revisionNoteIdSource as any;
            const asNum = Number(raw);
            tr.revisionNoteId = Number.isFinite(asNum) ? asNum : raw;
          }

          mappedPayload.transitionRequest = tr as any;
        }
      }
      // Unified convention: if sectionName is empty/missing, persist as "Untitled".
      mappedPayload.sectionName = normalizeSectionNameOrDefault((mappedPayload as any)?.sectionName);

      if (transitionOptions?.discontinueData) {
        (mappedPayload as any).discontinueData = transitionOptions.discontinueData;
        if (
          vendorAssessmentId != null &&
          String(vendorAssessmentId).trim() !== ""
        ) {
          const n = Number(vendorAssessmentId);
          (mappedPayload as any).vendorAssessmentId = Number.isFinite(n)
            ? n
            : String(vendorAssessmentId).trim();
        }
      }

      if (transitionOptions?.notProgressedData) {
        (mappedPayload as any).notProgressedData = transitionOptions.notProgressedData;
        if (
          vendorAssessmentId != null &&
          String(vendorAssessmentId).trim() !== ""
        ) {
          const n = Number(vendorAssessmentId);
          (mappedPayload as any).vendorAssessmentId = Number.isFinite(n)
            ? n
            : String(vendorAssessmentId).trim();
        }
      }

      const response = await PackagingService.updateProductSpecification(String(productSpecId), mappedPayload);
      const { Vendor, TJ } = JSON.parse(mappedPayload?.isOtherLabellingValidated || 'false');
      if((isVendor && Vendor) || (isTJ && TJ)) {
        updateSectionValidationFlag('otherLabeling', String(productSpecId));
      }
      // Do NOT replace form with API response; keep user's current form as-is
      return response;
    } catch (e: any) {
      setError(e?.message || e?.errorMessage || 'Failed to save packaging');
      throw e;
    } finally {
      setLoading(false);
      stopLoading();
    }
  }, [sectionId, resolveProductSpecificationId, vendorAssessmentId, userDetails, getPackagingReviewCompleteTransitionIds, getPackagingReviewAgainTransitionIds, getShareForReviewTransitionId, getPackagingRequestRevisionTransitionId, requestForRevisionNotes, normalizeAvailableTransitions]);

  const getPackagingComponentLevelType = useCallback((levelId: string): 'primary' | 'secondary' | 'tertiary' | null => {
    const levelsDropdown = dropdowns?.PackagingLevels || [];
    // Try to find the level name from dropdown data
    let levelName = levelId;
    if (levelsDropdown.length > 0) {
      const levelOption = levelsDropdown.find((option: any) => 
        String(option?.value || option?.id || '') === levelId
      );
      if (levelOption) {
        levelName = String(levelOption?.label || levelId);
      }
    }
    
    // Check the level name (case-insensitive)
    const levelLower = levelName.toLowerCase();
    if (levelLower.includes('primary')) {
      return 'primary';
    } else if (levelLower.includes('secondary')) {
      return 'secondary';
    } else if (levelLower.includes('tertiary')) {
      return 'tertiary';
    }
    
    return null;
  }, [dropdowns?.PackagingLevels]);

  const  calculateQuantitySeparable  = useCallback((components?: any[]) => {
    const componentsToUse = components || (Array.isArray(data?.packagingComponents) ? data.packagingComponents : []);
    
    let primaryCount = 0;
    let secondaryCount = 0;
    let tertiaryCount = 0;
    
    componentsToUse.forEach((component: any) => {
      const levelId = String(component?.level || '');
      const levelType = getPackagingComponentLevelType(levelId);
      
      if (levelType === 'primary') {
        primaryCount++;
      } else if (levelType === 'secondary') {
        secondaryCount++;
      } else if (levelType === 'tertiary') {
        tertiaryCount++;
      }
    });
    
    return {
      primaryPackaging: primaryCount,
      secondaryPackaging: secondaryCount,
      tertiaryPackaging: tertiaryCount
    };
  }, [data?.packagingComponents, getPackagingComponentLevelType]);
  
  const getMaterialSubclass = useCallback((materialId: string | undefined): any[] => {
    if(!materialId) return [];
    const packagingMaterial = dropdowns?.PackagingMaterial || [];
    const selectedPackagingMaterial: any = packagingMaterial.find((option: any) => 
      String(option?.value || '') === materialId
    );
    const materialSubclasses = selectedPackagingMaterial?.children || [];
    return Array.isArray(materialSubclasses) ? materialSubclasses : [];

  }, [dropdowns?.PackagingMaterial]);

  return {
    packaging: data,
    dropdowns,
    loading,
    error,
    savePackaging,
    requestForRevisionNotes,
    setRequestForRevisionNotes,
    loadPackaging,
    calculateQuantitySeparable,
    getPackagingComponentLevelType,
    getMaterialSubclass,
  };
};

export default usePackaging;


